import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { LogoMark, Wordmark } from "@/components/dealer/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "dealer_inbox_token";

const inputClass =
  "w-full border-b border-dealer-ivory/20 bg-transparent py-4 font-sans text-sm font-light text-dealer-ivory focus:border-dealer-burgundybright focus:outline-none transition-colors duration-500";

export default function Inbox() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (t) => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/inbox`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        setItems(data);
        setError("");
      } catch (e) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        setItems(null);
        setError("Session expired. Please sign in again.");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const onLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Sign in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const signOut = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setItems(null);
    setPassword("");
  };

  return (
    <div className="min-h-screen bg-dealer-black" data-testid="inbox-page">
      <div className="mx-auto max-w-3xl px-6 py-16 sm:py-24">
        <div className="flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="inbox-home-link">
            <LogoMark size={30} />
            <Wordmark className="text-lg" />
          </a>
          {token && (
            <button
              onClick={signOut}
              data-testid="inbox-signout-button"
              className="font-sans text-[11px] font-medium tracking-[0.25em] text-dealer-subtle transition-colors duration-300 hover:text-dealer-ivory"
            >
              SIGN OUT
            </button>
          )}
        </div>

        <p className="dealer-eyebrow mt-16">PRIVATE</p>
        <h1 className="mt-4 font-serif text-3xl tracking-tight text-dealer-ivory sm:text-4xl">
          ENQUIRY INBOX
        </h1>

        {!token ? (
          <form onSubmit={onLogin} className="mt-14 max-w-sm space-y-8" data-testid="inbox-login-form">
            <div>
              <label htmlFor="inbox-password" className="dealer-eyebrow mb-1 block">
                PASSKEY
              </label>
              <input
                id="inbox-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className={inputClass}
                data-testid="inbox-password-input"
              />
            </div>
            {error && (
              <p className="font-sans text-xs tracking-[0.1em] text-dealer-burgundybright" data-testid="inbox-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="inbox-login-button"
              className="group inline-flex items-center gap-4 border border-dealer-ivory/25 px-8 py-4 font-sans text-[11px] font-medium tracking-[0.3em] text-dealer-ivory transition-colors duration-500 hover:border-dealer-burgundybright hover:text-dealer-burgundybright disabled:opacity-50"
            >
              {loading ? "CHECKING…" : "ENTER"}
              <span className="block h-px w-8 bg-current transition-[width] duration-500 group-hover:w-12" />
            </button>
          </form>
        ) : (
          <div className="mt-14" data-testid="inbox-list">
            {loading && !items ? (
              <p className="font-sans text-sm font-light text-dealer-subtle">Loading…</p>
            ) : items && items.length === 0 ? (
              <p className="font-sans text-sm font-light text-dealer-subtle" data-testid="inbox-empty">
                No enquiries yet.
              </p>
            ) : (
              <ul className="divide-y divide-dealer-ivory/10">
                {(items || []).map((q, i) => (
                  <li key={q.id || i} className="py-8" data-testid={`inbox-entry-${i}`}>
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                      <p className="font-serif text-xl text-dealer-ivory">{q.name}</p>
                      <p className="font-sans text-[10px] tracking-[0.2em] text-dealer-subtle">
                        {q.timestamp ? new Date(q.timestamp).toLocaleString() : ""}
                      </p>
                    </div>
                    <p className="mt-2 font-sans text-xs tracking-[0.15em] text-dealer-silver">
                      {q.email}
                      {q.company ? `  ·  ${q.company}` : ""}
                    </p>
                    <p className="mt-4 whitespace-pre-wrap font-sans text-sm font-light leading-relaxed text-dealer-silver">
                      {q.message}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
